import { useState, useEffect, ReactNode, useContext, createContext, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box } from '@mui/material';
import { useInactivity } from '../hooks/useInactivity';
import type { User, OrganizationRole, Session, OrganizationSketch, OrganizationSketchActive } from '@/types';

export interface AuthContextType {
  user?: User;
  organizations: OrganizationSketch[];
  activeOrganization?: OrganizationSketchActive;
  activeRole?: OrganizationRole;
  isLoading: boolean;

  logout: () => Promise<void>;
  refreshSession: () => Promise<void>;
  switchOrganization: (organizationId: number) => Promise<void>;
}

export const AuthContext = createContext<AuthContextType | null>(null);

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const clearState = () => {
    setSession(null);
  };

  const logout = async () => {
    try {
      await fetch('/api/v1/auth/logout', { method: 'POST' });
    } finally {
      // Clear local state even if the API call fails
      clearState();
    }
  };

  const refreshSession = async () => {
    console.log('Refreshing session...');
    setIsLoading(true);
    try {
      const response = await fetch('/api/v1/auth/session');
      const data = (await response.json()) as Session & { valid: boolean };
      if (!data.valid) {
        clearState();
        return;
      }
      setSession(data);
    } catch (error) {
      console.error('Session check failed:', error);
      clearState();
    } finally {
      setIsLoading(false);
    }
  };

  const checkSession = useCallback(async () => {
    try {
      const response = await fetch('/api/v1/auth/session');
      if (!response.ok) {
        navigate('/login');
      }
    } catch (error) {
      console.error('Session check failed:', error);
      navigate('/login');
    }
  }, [navigate]);

  useInactivity(checkSession);

  const switchOrganization = async (organizationId: number) => {
    try {
      await fetch(`/api/v1/auth/switch`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ organizationId }),
      });
      await refreshSession();
    } catch (error) {
      console.error('Failed to switch organization:', error);
      throw error;
    }
  };

  useEffect(() => {
    refreshSession();
  }, []);

  // Create stable context value that doesn't change unless dependencies change
  const contextValue = {
    user: session?.user,
    organizations: session?.organizations || [],
    activeOrganization: session?.activeOrganization,
    activeRole: session?.activeRole,
    isLoading,
    logout,
    refreshSession,
    switchOrganization,
  };

  // Don't render anything until we know the auth state
  if (isLoading) {
    return <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }} />;
  }

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
}
